import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useFetchData } from 'services/api/useFetchData';
import { useModal } from 'components/modals/ModalContext';
import { useAllowedRight } from 'services/permissions/permissionChecks';
import { iconMapping } from 'services/utils/iconMapping';
import { EditableStatusType } from 'types/FieldTypes';
import IconButton from 'components/buttons/IconButton';
import AssetForm from '../../views/assets/AssetForm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import '../../style/scss/card.scss';

// Type of an object row on the card
interface CardAssetType {
    id: number;
    name: string;
    number?: string | null;
    asset_type?: number | null;
    asset_type_name?: string | null;
    asset_type_icon?: string | null;
    parent_asset?: number | null;
}

const AssetsCard: React.FC<{
    data?: any;
    loadStatus?: 'idle' | 'loading' | 'loaded';
    source?: 'contract' | 'location'; // The contract shows its selection, the location shows all its objects
    itemId?: string;
    isEditable?: EditableStatusType;
}> = ({
    data,
    loadStatus,
    source = 'contract',
    itemId,
    isEditable,
}) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const userHasOnlyViewRights = useAllowedRight('only_view');

    // Determine wheter the card shows all objects of a location
    const isLocationCard = source === 'location';
    const allAssets = isLocationCard || !!data?.all_assets;
    const locationId = isLocationCard ? itemId : data?.work_location?.id;

    // Fetch all objects of the location when the card covers them all
    const { response: fetchedLocationAssets, refetch } = useFetchData({
        apiUrl: allAssets && locationId ? `get_asset_search/${locationId}` : '',
        params: useMemo(() => ({}), []),
    });

    // Use the location objects or the objects selected on the contract
    const assets: CardAssetType[] = useMemo(() => {
        return allAssets
            ? fetchedLocationAssets?.results ?? []
            : data?.assets ?? [];
    }, [allAssets, fetchedLocationAssets, data?.assets]);

    // Group the objects under their parent object
    const childAssetMap = useMemo(() => {
        const assetIds = new Set(assets.map(asset => asset.id));
        const childMap = new Map<number, CardAssetType[]>();

        assets.forEach(asset => {
            if (asset.parent_asset && assetIds.has(asset.parent_asset)) {
                childMap.set(asset.parent_asset, [...(childMap.get(asset.parent_asset) || []), asset]);
            }
        });

        return childMap;
    }, [assets]);

    // Get the root objects whose parent is not on the card
    const rootAssets = useMemo(() => {
        const assetIds = new Set(assets.map(asset => asset.id));
        return assets.filter(asset => !asset.parent_asset || !assetIds.has(asset.parent_asset));
    }, [assets]);

    // Don't render the contract card when the contract has no object selection
    if (!isLocationCard && !allAssets && assets.length === 0) {
        return null;
    }

    // Open the object form linked to this location and refetch the card afterwards
    const openAddAssetModal = () => {
        if (!locationId) return;
        initializeModal(
            <AssetForm linkedItem={{ location: Number(locationId) }} />, {
                title: t('button.add_object_label', { object_name: t('asset.general.object_name.singular') }),
                modalSize: 'add-form-smaller',
                onSuccess: () => refetch(),
            }
        );
    };

    // Build the label with the number and object type between brackets
    const getAssetDetailsLabel = (asset: CardAssetType) => {
        const detailParts = [asset.number, asset.asset_type_name].filter(Boolean);
        return detailParts.length > 0 ? ` (${detailParts.join(' - ')})` : '';
    };

    // Render one object row with its child objects indented below it
    const renderAssetRow = (asset: CardAssetType, depth: number): React.ReactNode => (
        <React.Fragment key={asset.id}>
            <div className='card-list-item asset-row' style={{ paddingLeft: `${depth * 1.2}rem` }}>
                <div>
                    <span className='asset-icon'>
                        <FontAwesomeIcon icon={iconMapping(asset.asset_type_icon || 'layer-group')} />
                    </span>
                    <Link className='card-link' to={`/contacts/assets/${asset.id}`}>
                        {asset.name}
                    </Link>
                    {getAssetDetailsLabel(asset) && (
                        <span className='asset-details'>{getAssetDetailsLabel(asset)}</span>
                    )}
                </div>
            </div>
            {(childAssetMap.get(asset.id) || []).map(childAsset => renderAssetRow(childAsset, depth + 1))}
        </React.Fragment>
    );

    // Build the title of the card
    const getCardTitle = () => {
        if (isLocationCard) return t('asset.general.objects_card_label');
        return assets.length === 1
            ? t('contract.general.service_object_label')
            : t('contract.general.service_objects_label');
    };

    return (
        <div className='card linked-items-card assets-card'>
            <div className='header'>
                <h6>
                    {getCardTitle()}
                    {assets.length > 1 && (
                        <span> ({assets.length})</span>
                    )}
                </h6>
                {isLocationCard && isEditable?.editability !== false && !userHasOnlyViewRights && (
                    <IconButton
                        onClick={openAddAssetModal}
                        size='extra-small'
                        icon={<FontAwesomeIcon icon={faPlus} />}
                        tooltipText={t('button.add_object_label', { object_name: t('asset.general.object_name.singular') })}
                    />
                )}
            </div>
            {loadStatus === 'loading' ? (
                <div className="loader"></div>
            ) : (
                <>
                    {!isLocationCard && allAssets && (
                        <p className='assets-all-label'>
                            {t('contract.general.all_assets_selected_label')}
                        </p>
                    )}
                    {isLocationCard && loadStatus === 'loaded' && fetchedLocationAssets && assets.length === 0 && (
                        <p className='no-assets-label'>
                            {t('asset.general.no_assets_label')}
                        </p>
                    )}
                    {rootAssets.map(asset => renderAssetRow(asset, 0))}
                </>
            )}
        </div>
    );
};

export default AssetsCard;
